import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

import { APP_CONFIG } from '../../app.config';
import { OrderStatus } from '../../pages/orders/enums/order-status.enum';
import { PaymentStatus } from '../../pages/orders/enums/payment-status.enum';
import { ReservationStatus } from '../../pages/reservation/enums/reservation-status.enum';
import { ChartType } from '../enums/chart-type.enum';
import { UtilHelper } from '../helpers/utils.helper';
import { Chart, ChartComponent } from '../models/chart.model';
import { DashboardBilling } from '../models/dashboard-billing.model';
import { Error } from '../models/error.model';
import { RequestService } from './request.service';

@Injectable()
export class ChartService {

	constructor(
		private decimalPipe: DecimalPipe,
		private requestService: RequestService
	) { }

	getOrderSummay(startDate: string, endDate: string, establishmentId: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams(
				{ estabId: establishmentId, startDate: startDate, endDate: endDate },
				APP_CONFIG.WEBSERVICE.GET_ORDER_SUMMARY_DASHBOARD
			);
			this.requestService.getData(urlRequest).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getBiiling(startDate: string, endDate: string, establishmentId: number): Promise<Array<DashboardBilling>> {
		return new Promise((resolve, reject) => {
			let urlRequest: string = this.requestService.buildUrlPathParams(
				{
					estabId: establishmentId,
					startDate: startDate,
					endDate: endDate
				},
				APP_CONFIG.WEBSERVICE.GET_DASHBOARD_BIILING
			);
			urlRequest += "&paymentStatus=" + PaymentStatus.PAID;

			const filterOrderStatus: Array<OrderStatus> = this.getOrdersStatus();
			urlRequest += "&orderStatus=" + filterOrderStatus.join(",");

			this.requestService.getData(urlRequest).then((response: Array<DashboardBilling>) => {
				console.log(response);
				resolve(response || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getOrders(startDate: string, endDate: string, establishmentId: number): Promise<any> {
		return new Promise((resolve, reject) => {
			let urlRequest: string = this.requestService.buildUrlPathParams(
				{ estabId: establishmentId, startDate: startDate, endDate: endDate },
				APP_CONFIG.WEBSERVICE.GET_DASHBOARD_ORDERS
			);
			urlRequest += "&paymentStatus=" + PaymentStatus.PAID + "," + PaymentStatus.CANCELLED + "," + PaymentStatus.REVERSED;

			const filterOrderStatus: Array<OrderStatus> = this.getOrdersStatus();
			urlRequest += "&orderStatus=" + filterOrderStatus.join(",");

			this.requestService.getData(urlRequest).then((response: Array<any>) => {
				resolve(response || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	resetSummaryOrderChart(): any {
		return {
			total: 0,
			finished: 0,
			cancelled: 0
		};
	}

	buildLineOrderChartData(resultResponse: Array<any>, translateService: TranslateService): Array<any> {
		// Primeiro, prepara os status agrupando os finalizados (FINISHED, RATED e DELIVERED)
		const lineChartData: Array<any> = [];
		resultResponse.forEach((response) => {
			const orderStatus: OrderStatus = UtilHelper.getEnumByCod(response.status, OrderStatus);
			const description: string = translateService.instant(orderStatus["chartLabel"]);

			//remove html tags
			const statusName: string = description.replace(/(<([^>]+)>)/gi, "");
			const status: number = lineChartData.find((d: any) => d.name === statusName);
			if (!status) {
				lineChartData.push({
					name: statusName,
					series: [],
					color: orderStatus['chartColor']
				});
			}
		});

		// Lista auxiliar, para saber se a data do pedido já foi inserida
		const chartDates: Array<string> = [];

		resultResponse.forEach((response) => {
			const orderStatus: OrderStatus = UtilHelper.getEnumByCod(response.status, OrderStatus);
			const description: string = translateService.instant(orderStatus["chartLabel"]);

			//remove html tags
			const statusName: string = description.replace(/(<([^>]+)>)/gi, "");

			response.orders.forEach((order) => {
				const date: string = moment(order.date, "YYYY-MM-DD", true).format("ddd DD/MM");

				// Se a data ainda não foi usada, adiciona ela em todos os status com valor default 0
				const chartDate: string = chartDates.find((d: string) => d === date);
				if (!chartDate) {
					chartDates.push(date);
					lineChartData.forEach((d: any) => {
						d.series.push({
							name: date,
							value: 0
						});
					});
				}
				// Busca o index do status na lista, e o index da data, e soma ao valor total de orders
				const statusIndex: number = lineChartData.findIndex((d: any) => d.name === statusName);
				const dateIndex: number = lineChartData[statusIndex].series.findIndex((d: any) => d.name === date);
				lineChartData[statusIndex].series[dateIndex].value += order.total;
			});
		});
		return lineChartData;
	}

	getCustomColorsChart(data: Array<any>): Array<any> {
		const customColors: Array<any> = [];
		data.forEach((d: any) => {
			customColors.push({
				name: d.name,
				value: d.color
			});
		});
		return customColors;
	}

	private getOrdersStatus(): Array<OrderStatus> {
		return [
			// OrderStatus.IN_QUEUE['code'],
			// OrderStatus.IN_PRODUCTION['code'],
			// OrderStatus.PARTIAL_DELIVERED['code'],
			// OrderStatus.FINISHED['code'],
			OrderStatus.RATED['code'],
			OrderStatus.DELIVERED['code'],
			OrderStatus.CANCELED_BY_ESTABLISHMENT['code'],
			OrderStatus.CANCELED_BY_CUSTOMER['code']
		];
	}

	// ----- NOVOS GRÁFICOS --------

	getChartsData(startDate: string, endDate: string, byPlace: boolean, type: ChartType, placeId?: number, stores?: Array<number>, limit?: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const params: any = {
				startDate: startDate,
				endDate: endDate,
				byPlace: byPlace,
				type: type,
				status: "DELIVERED,RATED"
			};
			if (placeId) params.place = placeId;
			// if (orderStatus) params.orderStatus = orderStatus.join(",");
			if (stores) params.stores = stores.join(",");
			if (limit) params.limit = limit;

			// buildUrlParams
			const urlRequest: string = APP_CONFIG.WEBSERVICE.GET_CHART + this.requestService.buildUrlParams(params);
			console.log(urlRequest);
			this.requestService.getData(urlRequest).then((response: Array<any>) => {
				console.log(response);
				resolve(response || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getReservationChartsData(startDate: string, endDate: string, type: ChartType, store: number, status?: Array<ReservationStatus>, addPresent?: boolean): Promise<any> {
		return new Promise((resolve, reject) => {
			const params: any = {
				startDate: startDate,
				endDate: endDate,
				type: type,
				stores: store
			};
			if (status) params.status = status.join(",");
			if (addPresent) params.addPresent = true;

			// buildUrlParams
			const urlRequest: string = APP_CONFIG.WEBSERVICE.GET_CHART + this.requestService.buildUrlParams(params);
			console.log(urlRequest);
			this.requestService.getData(urlRequest).then((response: Array<any>) => {
				console.log(response);
				resolve(response || []);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	buildChartData(resultResponse: Array<Chart>, type: ChartType, translateService: TranslateService, isStore: boolean, chart: any, reservationStatus?: Array<ReservationStatus>): any {
		let result: Array<Chart> = [];
		switch (type) {
			case ChartType.AVERAGE_TICKET:
				resultResponse.forEach((cr: Chart) => {
					cr.value = this.decimalPipe.transform(cr.value, '.2-2');
					result.push(cr);
				});
				if (isStore && result.length > 0) {
					return result[0];
				}
				break;

			case ChartType.DELIVERY_FEE:
				if (resultResponse.length > 0) {
					return resultResponse[0];
				}
				break;

			case ChartType.BY_PICKUP_OPTION:
				resultResponse.forEach((cr: Chart) => {
					if (cr.series) {
						cr.series.forEach((item: Chart) => {
							item.name = translateService.instant("ORDERS." + item.name);
						});
					} else {
						cr.series = [];
					}
					result.push(cr);
				});
				if (isStore && result.length > 0) {
					this.setPieChartColorsLegend(chart, result[0].series);
					return result[0].series;
				}
				break;

			case ChartType.BY_PAYMENT_TYPE:
				resultResponse.forEach((cr: Chart) => {
					if (cr.series) {
						cr.series.forEach((item: Chart) => {
							item.name = translateService.instant("PAYMENT." + item.name);
						});
					} else {
						cr.series = [];
					}
					result.push(cr);
				});
				if (isStore && result.length > 0) {
					this.setPieChartColorsLegend(chart, result[0].series);
					return result[0].series;
				}
				break;

			case ChartType.ORDERS_BY_DATE:
				chart.total = 0;
				if (isStore) {
					resultResponse.forEach((cr: Chart) => {
						cr.name = "Pedidos finalizados";
						if (cr.series) {
							cr.series.forEach((item: Chart) => {
								const date: string = moment(item.name, "YYYY-MM-DD", true).format("ddd DD/MM");
								item.name = date;
								chart.total += item.value;
							});
						} else {
							cr.series = [];
						}
						result.push(cr);
					});
				} else {
					result = this.prepareDataToLineChartDate(resultResponse, translateService, chart, false);
				}
				break;

			case ChartType.SALES_BY_STORE:
				if (isStore) {
					chart.totalFinished = 0;
					chart.totalCanceled = 0;
					chart.legendColors = [];

					resultResponse.forEach((cr: Chart) => {
						if (cr.series) {
							cr.series.forEach((item: Chart) => {
								const nameTranslated: string = translateService.instant("STATISTICS." + item.name);
								if (item.name == 'FINISHED') {
									chart.totalFinished += item.value;
									chart.legendColors.push({
										name: nameTranslated,
										value: '#4CAF50'
									});
								} else if (item.name == 'CANCELED') {
									chart.totalCanceled += item.value;
									chart.legendColors.push({
										name: nameTranslated,
										value: '#EF5350'
									});
								}
								item.name = nameTranslated;
							});
						} else {
							cr.series = [];
						}
						result.push(cr);
					});
					if (result.length > 0) {
						return result[0].series;
					}
				} else {
					chart.totalFinished = 0;
					chart.totalCanceled = 0;

					chart.finishedData = [];
					chart.canceledData = [];

					/**
					 * Constroi de forma separada os itens finalizados e cancelados
					 */
					resultResponse.forEach((cr: Chart) => {
						if (cr.series) {
							cr.series.forEach((item: Chart) => {
								if (item.name == 'FINISHED') {
									chart.totalFinished += item.value;
									chart.finishedData.push({
										name: cr.name,
										value: item.value
									});
								} else if (item.name == 'CANCELED') {
									chart.totalCanceled += item.value;
									chart.canceledData.push({
										name: cr.name,
										value: item.value
									});
								}
							});
						}
					});
					if (chart.segmentSelected == 'finished') {
						chart.total = chart.totalFinished;
						chart.data = chart.finishedData;
					} else {
						chart.total = chart.totalCanceled;
						chart.data = chart.canceledData;
					}
					return chart.data;
				}
				break;

			case ChartType.SALES_BY_PRODUCT:
				chart.originalData = [];
				resultResponse.forEach((cr: Chart) => {
					if (!cr.series) {
						cr.series = [];
					}
					chart.originalData.push(cr);
				});
				// Retorna apenas o primeiro store da lista para popular a lista q será usada no gráfico
				if (chart.originalData.length > 0) {
					chart.selectedItem = chart.originalData[0];
					return chart.selectedItem.series.slice(0, 10);
				}
				break;

			case ChartType.RESERVATION_TOTAL:
				chart.totalPresent = 0;
				chart.totalApproved = 0;
				chart.totalCanceled = 0;

				const isApprovedChart: boolean = reservationStatus.filter((s) => s == ReservationStatus.APPROVED).length > 0;

				if (isApprovedChart) {
					const approved: Chart = resultResponse.find((item) => item.name == ReservationStatus.APPROVED);
					if (approved) { chart.totalApproved = approved.value; }

					const approvedWithPresent: Chart = resultResponse.find((item) => item.name == "PRESENT");
					if (approvedWithPresent) { chart.totalPresent = approvedWithPresent.value; }

				} else {
					const canceledByStore: Chart = resultResponse.find((item) => item.name == ReservationStatus.CANCELED_BY_STORE);
					if (canceledByStore) { chart.totalCanceled += canceledByStore.value; }

					const canceledByCustomer: Chart = resultResponse.find((item) => item.name == ReservationStatus.CANCELED_BY_CUSTOMER);
					if (canceledByCustomer) { chart.totalCanceled += canceledByCustomer.value; }

					chart.legendColors = [];
					resultResponse.forEach((item: Chart) => {
						if (item.value) {
							const nameTranslated: string = translateService.instant("STATISTICS." + item.name);
							if (item.name == ReservationStatus.CANCELED_BY_STORE) {
								chart.legendColors.push({
									name: nameTranslated,
									value: '#EF5350'
								});
							} else if (item.name == ReservationStatus.CANCELED_BY_CUSTOMER) {
								chart.legendColors.push({
									name: nameTranslated,
									value: '#FFCA28'
								});
							}
							item.name = nameTranslated;
							result.push(item);
						}
					});
				}
				break;

			case ChartType.RESERVATION_CREATED_BY:
				chart.legendColors = [];
				resultResponse.forEach((item: Chart) => {
					if (item.value) {
						const nameTranslated: string = translateService.instant("STATISTICS." + item.name);
						if (item.name == 'CREATED_BY_STORE') {
							chart.legendColors.push({
								name: nameTranslated,
								value: '#EF5350'
							});
						} else if (item.name == 'CREATED_BY_CUSTOMER') {
							chart.legendColors.push({
								name: nameTranslated,
								value: '#FFCA28'
							});
						}
						item.name = nameTranslated;
						result.push(item);
					}
				});
				break;

			case ChartType.TOP_DAYS_SALES:
			case ChartType.RESERVATION_TOP_DAYS:
				result.push(
					{ name: "SUNDAY", value: 0 },
					{ name: "MONDAY", value: 0 },
					{ name: "TUESDAY", value: 0 },
					{ name: "WEDNESDAY", value: 0 },
					{ name: "THURSDAY", value: 0 },
					{ name: "FRIDAY", value: 0 },
					{ name: "SATURDAY", value: 0 }
				);
				result.forEach((c) => {
					const day: Chart = resultResponse.find((cDay) => cDay.name == c.name);
					if (day) {
						c.value = day.value;
					}
					c.name = translateService.instant("STATISTICS." + c.name);
				});
				console.log("RESERVATION_TOP_DAYS", result);
				break;

			case ChartType.RESERVATION_BY_DATE:
				result = this.prepareDataToLineChartDate(resultResponse, translateService, chart, true);
				break;

			default:
				return resultResponse;
		}
		return result;
	}

	private addZeroToItemDate(dates: Array<string>, chart: Chart, format: boolean): void {
		dates.forEach((d: string) => {
			chart.series.push({
				name: format ? moment(d, "YYYY-MM-DD", true).format("ddd DD/MM") : d,
				value: 0
			});
		});
	}

	private setPieChartColorsLegend(chart: any, series: Array<Chart>): void {
		chart.legendColors = [];
		// ChartComponent.defaultColorScheme.domain
		for (let i: number = 0; i < series.length; i++) {
			chart.legendColors.push({
				name: series[i].name,
				value: ChartComponent.defaultColorScheme.domain[i]
			});
		}
		console.log("Legend Colors", chart.legendColors);
	}

	private prepareDataToLineChartDate(resultResponse: Array<Chart>, translateService: TranslateService, chart: any, translateName: boolean): Array<Chart> {
		const result: Array<Chart> = [];
		// Lista auxiliar, para saber se a data do pedido já foi inserida
		const chartDates: Array<string> = [];

		// For para buscar todas as datas disponíveis
		resultResponse.forEach((cr: Chart) => {
			if (cr.series) {
				cr.series.forEach((item: Chart) => {
					const date: string = chartDates.find((d: string) => d === item.name);
					// Se a data ainda não existe na lista auxiliar, adiciono
					if (!date) {
						chartDates.push(item.name);
					}
				});
			}
		});
		if (chartDates.length > 0) {
			// Ordena as datas
			chartDates.sort((a, b) => a > b ? 1 : (b > a ? -1 : 0));

			// Formato as datas e adiciono onde não tem
			resultResponse.forEach((cr: Chart) => {
				if (cr.series) {
					const seriesDates: Array<string> = cr.series.map((c) => c.name);
					// Obtem as datas que não estão na lista series
					const dates: Array<string> = chartDates.filter((d: string) => seriesDates.indexOf(d) == -1);
					// Adiciono valor zero nas datas que não tem pedidos
					this.addZeroToItemDate(dates, cr, false);

					// Nesse momento tenho todas as datas no series, porém elas podem não estar ordenadas
					cr.series.sort((a, b) => a.name > b.name ? 1 : (b.name > a.name ? -1 : 0));

					// Formato as datas
					cr.series.forEach((item: Chart) => {
						const date: string = moment(item.name, "YYYY-MM-DD", true).format("ddd DD/MM");
						item.name = date;
						chart.total += item.value;
					});
				} else {
					cr.series = [];
					// Adiciono as datas nos stores que vieram zerados
					this.addZeroToItemDate(chartDates, cr, true);
				}
				if (translateName) {
					cr.name = translateService.instant("STATISTICS." + cr.name);
				}
				result.push(cr);
			});
		}
		return result;
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/chart.service.ts